import React, { useEffect, useState, useMemo } from "react";
import { api, apiErrorMessage, formatCurrency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { toast } from "sonner";
import { Plus, X, CaretDown, MagnifyingGlass } from "@phosphor-icons/react";

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function NayaOrderDialog({ open, onOpenChange, onCreated }) {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [form, setForm] = useState(defaultForm());
    const [saving, setSaving] = useState(false);

    function defaultForm() {
        return {
            customer_name: "",
            mobile: "",
            address: "",
            order_date: todayISO(),
            dispatch_date: "",
            items: [{ product_id: "", hsn_code: "", quantity: "", unit_price: "", gst_percent: 18 }],
            delivery_charge: 0,
            transportation_charge: 0,
            advance_amount: 0,
            advance_mode: "cash",
            notes: "",
        };
    }

    useEffect(() => {
        if (!open) return;
        setForm(defaultForm());
        setCategoryFilter("all");
        (async () => {
            try {
                const [{ data: prods }, { data: cats }] = await Promise.all([
                    api.get("/products", { params: { limit: 500, is_active: true } }),
                    api.get("/categories"),
                ]);
                setProducts(prods);
                setCategories(cats);
            } catch (e) { toast.error(apiErrorMessage(e)); }
        })();
    }, [open]);

    const filteredProducts = useMemo(() => {
        if (categoryFilter === "all") return products;
        return products.filter((p) => p.category_id === categoryFilter);
    }, [products, categoryFilter]);

    const categoryMap = useMemo(() => {
        const m = {};
        for (const c of categories) m[c.id] = c.name;
        return m;
    }, [categories]);

    const totals = useMemo(() => {
        let subtotal = 0, tax = 0;
        for (const it of form.items) {
            const line = Number(it.quantity || 0) * Number(it.unit_price || 0);
            subtotal += line;
            tax += line * Number(it.gst_percent || 0) / 100;
        }
        const dc = Number(form.delivery_charge || 0);
        const tc = Number(form.transportation_charge || 0);
        const grand = subtotal + tax + dc + tc;
        const advance = Number(form.advance_amount || 0);
        return {
            subtotal, tax, dc, tc, grand,
            advance,
            remaining: Math.max(0, grand - advance),
        };
    }, [form.items, form.advance_amount, form.delivery_charge, form.transportation_charge]);

    const updateItem = (idx, patch) => setForm((f) => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, ...patch } : it) }));
    const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { product_id: "", hsn_code: "", quantity: "", unit_price: "", gst_percent: 18 }] }));
    const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

    const onProductSelect = (idx, productId) => {
        const p = products.find((x) => x.id === productId);
        updateItem(idx, {
            product_id: productId,
            hsn_code: p?.hsn_code || "",
            unit_price: p ? Number(p.selling_price || 0) : 0,
            gst_percent: p ? Number(p.gst_percent || 18) : 18,
        });
    };

    const submit = async () => {
        if (!form.customer_name.trim()) return toast.error("Customer name zaruri hai");
        const validItems = form.items.filter((it) => it.product_id && Number(it.quantity) > 0);
        if (validItems.length === 0) return toast.error("Kam se kam ek product add karo");

        setSaving(true);
        try {
            const payload = {
                customer_name: form.customer_name.trim(),
                mobile: form.mobile.replace(/[^0-9+]/g, ""),
                address: form.address,
                order_date: form.order_date ? new Date(form.order_date).toISOString() : null,
                dispatch_date: form.dispatch_date ? new Date(form.dispatch_date).toISOString() : null,
                items: validItems.map((it) => ({
                    product_id: it.product_id,
                    quantity: Number(it.quantity),
                    unit_price: Number(it.unit_price),
                    gst_percent: Number(it.gst_percent || 0),
                    hsn_code: it.hsn_code || "",
                })),
                advance_amount: Number(form.advance_amount || 0),
                advance_mode: form.advance_mode,
                delivery_charge: Number(form.delivery_charge || 0),
                transportation_charge: Number(form.transportation_charge || 0),
                notes: form.notes,
            };
            const { data } = await api.post("/orders/quick", payload);
            toast.success(`Order ${data.order.order_no} created!`);
            onOpenChange(false);
            onCreated && onCreated(data.order);
        } catch (e) {
            toast.error(apiErrorMessage(e));
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="naya-order-dialog">
                <DialogHeader>
                    <DialogTitle className="text-2xl font-extrabold font-display tracking-tight">Naya Order</DialogTitle>
                </DialogHeader>

                {/* Customer section */}
                <div className="eyebrow -mt-2">Customer</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                        <Label className="text-sm font-semibold text-neutral-800">Customer Naam</Label>
                        <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                               className="mt-1.5 h-11" autoFocus data-testid="naya-name" />
                    </div>
                    <div>
                        <Label className="text-sm font-semibold text-neutral-800">Mobile Number</Label>
                        <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                               placeholder="+91 98xxxxxxxx" className="mt-1.5 h-11" data-testid="naya-mobile" />
                    </div>
                </div>
                <div>
                    <Label className="text-sm font-semibold text-neutral-800">Poora Address</Label>
                    <Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                              placeholder="House / shop, street, city, state, pincode" rows={2} className="mt-1.5" data-testid="naya-address" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                        <Label className="text-sm font-semibold text-neutral-800">Order Date</Label>
                        <Input type="date" value={form.order_date} onChange={(e) => setForm({ ...form, order_date: e.target.value })} className="mt-1.5 h-11" data-testid="naya-order-date" />
                    </div>
                    <div>
                        <Label className="text-sm font-semibold text-neutral-800">Dispatch Date (planned)</Label>
                        <Input type="date" value={form.dispatch_date} onChange={(e) => setForm({ ...form, dispatch_date: e.target.value })} className="mt-1.5 h-11" data-testid="naya-dispatch-date" />
                    </div>
                </div>

                {/* Products section */}
                <div className="flex items-center justify-between mt-2">
                    <div className="eyebrow">Products</div>
                    <Button variant="outline" size="sm" onClick={addItem} className="rounded-full" data-testid="naya-add-item">
                        <Plus size={14} className="mr-1" /> Item Jodein
                    </Button>
                </div>
                {categories.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap -mt-1" data-testid="naya-category-filter">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-neutral-500">Category:</span>
                        <button
                            type="button"
                            onClick={() => setCategoryFilter("all")}
                            className={`text-xs px-3 py-1 rounded-full border transition ${categoryFilter === "all" ? "bg-neutral-900 text-white border-neutral-900" : "bg-white text-neutral-700 border-neutral-200 hover:border-neutral-400"}`}
                            data-testid="naya-cat-all"
                        >
                            All ({products.length})
                        </button>
                        {categories.map((c) => {
                            const count = products.filter((p) => p.category_id === c.id).length;
                            if (count === 0) return null;
                            return (
                                <button
                                    key={c.id}
                                    type="button"
                                    onClick={() => setCategoryFilter(c.id)}
                                    className={`text-xs px-3 py-1 rounded-full border transition ${categoryFilter === c.id ? "bg-neutral-900 text-white border-neutral-900" : "bg-white text-neutral-700 border-neutral-200 hover:border-neutral-400"}`}
                                    data-testid={`naya-cat-${c.id}`}
                                >
                                    {c.name} ({count})
                                </button>
                            );
                        })}
                    </div>
                )}
                <div className="space-y-2">
                    {form.items.map((it, idx) => (
                        <div key={idx} className="grid grid-cols-12 gap-2 items-center rounded-md p-3 border border-neutral-100" style={{ background: "#F5F7F2" }} data-testid={`naya-item-${idx}`}>
                            <div className="col-span-12 md:col-span-3">
                                <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500 mb-1">Product</div>
                                <ProductPicker
                                    products={filteredProducts}
                                    categoryMap={categoryMap}
                                    value={it.product_id}
                                    onSelect={(pid) => onProductSelect(idx, pid)}
                                    testId={`naya-product-picker-${idx}`}
                                />
                            </div>
                            <div className="col-span-6 md:col-span-2">
                                <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500 mb-1">HSN</div>
                                <Input value={it.hsn_code || ""} onChange={(e) => updateItem(idx, { hsn_code: e.target.value })} className="h-10 bg-white font-mono text-xs" placeholder="7019" />
                            </div>
                            <div className="col-span-3 md:col-span-1">
                                <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500 mb-1">Qty</div>
                                <Input type="number" value={it.quantity} onChange={(e) => updateItem(idx, { quantity: e.target.value })} className="h-10 bg-white text-center px-1" />
                            </div>
                            <div className="col-span-3 md:col-span-2">
                                <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500 mb-1">Rate ₹</div>
                                <Input type="number" value={it.unit_price} onChange={(e) => updateItem(idx, { unit_price: e.target.value })} className="h-10 bg-white" />
                            </div>
                            <div className="col-span-4 md:col-span-2">
                                <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500 mb-1">GST %</div>
                                <Input type="number" value={it.gst_percent} onChange={(e) => updateItem(idx, { gst_percent: e.target.value })} className="h-10 bg-white text-center px-1" data-testid={`naya-gst-${idx}`} />
                            </div>
                            <div className="col-span-6 md:col-span-1 text-right">
                                <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500 mb-1">Total</div>
                                <div className="h-10 flex items-center justify-end tabular font-bold text-neutral-900" style={{ color: "#15803D" }}>
                                    {formatCurrency(Number(it.quantity || 0) * Number(it.unit_price || 0) * (1 + Number(it.gst_percent || 0) / 100))}
                                </div>
                            </div>
                            <button onClick={() => removeItem(idx)} disabled={form.items.length === 1}
                                    className="col-span-2 md:col-span-1 h-10 flex items-center justify-center text-neutral-500 hover:text-red-600 disabled:opacity-30" data-testid={`naya-remove-${idx}`}>
                                <X size={16} />
                            </button>
                        </div>
                    ))}
                </div>

                {/* Delivery + Transportation charges */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-1">
                    <div>
                        <Label className="text-sm font-semibold text-neutral-800">Delivery Charge ₹</Label>
                        <Input type="number" value={form.delivery_charge}
                               onChange={(e) => setForm({ ...form, delivery_charge: e.target.value })}
                               placeholder="0" className="mt-1.5 h-11" data-testid="naya-delivery-charge" />
                    </div>
                    <div>
                        <Label className="text-sm font-semibold text-neutral-800">Transportation Charge ₹</Label>
                        <Input type="number" value={form.transportation_charge}
                               onChange={(e) => setForm({ ...form, transportation_charge: e.target.value })}
                               placeholder="0" className="mt-1.5 h-11" data-testid="naya-transportation-charge" />
                    </div>
                </div>

                {/* Advance + totals */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
                    <div>
                        <Label className="text-sm font-semibold text-neutral-800">Advance Payment ₹</Label>
                        <Input type="number" value={form.advance_amount} onChange={(e) => setForm({ ...form, advance_amount: e.target.value })} className="mt-1.5 h-11" data-testid="naya-advance" />
                        {Number(form.advance_amount || 0) > 0 && (
                            <Select value={form.advance_mode} onValueChange={(v) => setForm({ ...form, advance_mode: v })}>
                                <SelectTrigger className="mt-2 h-10"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="cash">Cash</SelectItem>
                                    <SelectItem value="upi">UPI</SelectItem>
                                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                                    <SelectItem value="cheque">Cheque</SelectItem>
                                    <SelectItem value="cod">Cash on Delivery (COD)</SelectItem>
                                </SelectContent>
                            </Select>
                        )}
                    </div>
                    <div className="rounded-md p-4" style={{ background: "#F5F7F2" }} data-testid="naya-total-summary">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500">Total Amount</div>
                        <div className="text-xl font-extrabold font-display tabular mt-1" style={{ color: "#15803D" }}>{formatCurrency(totals.grand)}</div>
                        <div className="text-[10px] text-neutral-500 mt-1 space-y-0.5">
                            <div>Subtotal {formatCurrency(totals.subtotal)} · GST {formatCurrency(totals.tax)}</div>
                            {(totals.dc > 0 || totals.tc > 0) && (
                                <div>
                                    {totals.dc > 0 && <span>Delivery +{formatCurrency(totals.dc)} </span>}
                                    {totals.tc > 0 && <span>Transport +{formatCurrency(totals.tc)}</span>}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="rounded-md p-4" style={{ background: "#F5F7F2" }} data-testid="naya-remaining-summary">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500">Remaining Payment</div>
                        <div className="text-xl font-extrabold font-display tabular mt-1" style={{ color: totals.remaining > 0 ? "#DC2626" : "#15803D" }}>{formatCurrency(totals.remaining)}</div>
                        <div className="text-[10px] text-neutral-500 mt-1">Advance {formatCurrency(totals.advance)}</div>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="naya-cancel">Cancel</Button>
                    <Button onClick={submit} disabled={saving} className="text-white" style={{ backgroundColor: "#15803D" }} data-testid="naya-submit">
                        {saving ? "Saving..." : "Save Order"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ProductPicker({ products, categoryMap, value, onSelect, testId }) {
    const [open, setOpen] = useState(false);
    const selected = products.find((p) => p.id === value);
    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    role="combobox"
                    aria-expanded={open}
                    className="h-10 w-full flex items-center justify-between rounded-md border border-neutral-200 bg-white px-3 text-sm hover:border-neutral-400 focus:outline-none focus:ring-2 focus:ring-neutral-300"
                    data-testid={testId}
                >
                    <span className={selected ? "text-neutral-900 truncate" : "text-neutral-400"}>
                        {selected ? `${selected.name} · Stock ${selected.current_stock}` : "Product chunein"}
                    </span>
                    <CaretDown size={14} className="ml-2 shrink-0 opacity-60" />
                </button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-[min(92vw,420px)]" align="start">
                <Command
                    filter={(itemValue, search) => {
                        const q = search.toLowerCase().trim();
                        if (!q) return 1;
                        return itemValue.toLowerCase().includes(q) ? 1 : 0;
                    }}
                >
                    <div className="flex items-center border-b border-neutral-200 px-3">
                        <MagnifyingGlass size={14} className="opacity-50 shrink-0" />
                        <CommandInput placeholder="Search by name / SKU / barcode..." className="border-0 focus:ring-0 h-11" />
                    </div>
                    <CommandList className="max-h-72">
                        <CommandEmpty className="py-6 text-center text-xs text-neutral-500">
                            Koi product nahi mila
                        </CommandEmpty>
                        <CommandGroup>
                            {products.map((p) => {
                                const catName = categoryMap[p.category_id] || "";
                                const searchable = `${p.name} ${p.sku || ""} ${p.barcode || ""} ${catName}`;
                                return (
                                    <CommandItem
                                        key={p.id}
                                        value={searchable}
                                        onSelect={() => {
                                            onSelect(p.id);
                                            setOpen(false);
                                        }}
                                        className="cursor-pointer flex items-center justify-between gap-2 py-2"
                                        data-testid={`naya-product-option-${p.id}`}
                                    >
                                        <div className="flex flex-col min-w-0">
                                            <span className="font-medium truncate">{p.name}</span>
                                            <span className="text-[11px] text-neutral-500 font-mono">
                                                {p.sku || "—"}{catName ? ` · ${catName}` : ""}
                                            </span>
                                        </div>
                                        <span className="text-[11px] font-semibold shrink-0 tabular" style={{ color: p.current_stock <= 0 ? "#DC2626" : "#15803D" }}>
                                            Stock {p.current_stock}
                                        </span>
                                    </CommandItem>
                                );
                            })}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
